import React, { useEffect, useState } from 'react';
import { Button, Divider, Flex, Form, Input, Select, Switch, Typography } from 'antd';
import type { FormInstance } from 'antd';
import { MinusCircleOutlined, CheckCircleOutlined, EditOutlined } from '@ant-design/icons';
import { FormContainer, FormSectionTitle } from '@leedpulse-monorepo/components';
import { DEMO_DOMAINS } from '@leedpulse-monorepo/consts';
import { normalizeString } from '@leedpulse-monorepo/utils';
import AvatarInput from '../../@shared/AvatarInput';
import { formatDefinedDemoEmail } from './definedDemoMail';
import { WIDE_BORDER_WIDTH } from './DemoAccountForm';
import DomainsSelect from '../../domains_page/select/DomainsSelect';

interface DemoAccountFormItemProps {
    field: { name: number; key: number };
    index: number;
    fieldsCount: number;
    form: FormInstance;
    datasets: string[];
    defaultDatasetFilename?: string;
    datasetsLoaded: boolean;
    creationMode: 'single' | 'multiple';
    accountResult?: { success: boolean; errorMessage?: string };
    onRemove: () => void;
}

const DemoAccountFormItem: React.FC<DemoAccountFormItemProps> = ({
    field,
    index,
    fieldsCount,
    form,
    datasets,
    defaultDatasetFilename,
    datasetsLoaded,
    creationMode,
    accountResult,
    onRemove,
}) => {
    const isReadOnly = Boolean(accountResult?.success);

    const definedMailEnabled = Form.useWatch('defined_mail_enabled', form) ?? false;
    const definedMailBase = Form.useWatch(['defined_mail', 'mail_base'], form) as string | undefined;
    const definedMailOffset = Form.useWatch(['defined_mail', 'mail_offset'], form) as number | undefined;
    const definedMailDomain = Form.useWatch(['defined_mail', 'mail_domain'], form) as string | undefined;
    const definedMailCountry = Form.useWatch(['defined_mail', 'country_code'], form) as string | undefined;
    const emailManualEnabled = Form.useWatch(['accounts', index, 'user', 'email_manual_enabled'], form) as boolean | undefined;

    const definedMailPreview =
        definedMailEnabled &&
        [definedMailBase, definedMailDomain, definedMailCountry].every((s) => String(s ?? '').trim())
            ? formatDefinedDemoEmail(
                String(definedMailBase ?? ''),
                index,
                String(definedMailDomain ?? ''),
                String(definedMailCountry ?? ''),
                Math.max(0, Number(definedMailOffset ?? 0) || 0),
            )
            : null;
    const isManualEmail = Boolean(emailManualEnabled);

    const toggleEmailEdition = () => {
        if (isReadOnly) return;

        if (!isManualEmail) {
            const firstname = String(form.getFieldValue(['accounts', index, 'user', 'firstname']) ?? '');
            const lastname = String(form.getFieldValue(['accounts', index, 'user', 'lastname']) ?? '');
            const emailDomain = String(form.getFieldValue(['accounts', index, 'user', 'email_domain']) ?? '');
            const local = `${normalizeString(firstname)}.${normalizeString(lastname)}`
                .replace(/\.+/g, '.')
                .replace(/^\./, '')
                .replace(/\.$/, '');

            form.setFieldValue(['accounts', index, 'user', 'email'], `${local}@${emailDomain}`);
        }

        form.setFieldValue(['accounts', index, 'user', 'email_manual_enabled'], !isManualEmail);
    };

    const [isNewDataset, setIsNewDataset] = useState<boolean | null>(() => {
        const current = form.getFieldValue(['accounts', index, 'new_dataset']);
        return typeof current === 'boolean' ? current : null;
    });

    // `isNewDataset` reste du state (l'interrupteur est basculable), mais sa
    // valeur par defaut se derive des datasets. La garde porte sur `=== null`,
    // donc elle converge et se pose AUSSI pour une ligne ajoutee apres le fetch —
    // une garde sur un changement de cle ne tirait pas dans ce cas, et la cle
    // `${datasetsLoaded}:${datasets.length}` collisionnait en plus sur un ajout
    // + un retrait entre deux rendus.
    if (datasetsLoaded && isNewDataset === null) {
        setIsNewDataset(datasets.length === 0);
    }

    useEffect(() => {
        // Keep form value in sync with local switch state.
        if (isNewDataset === null) return;
        form.setFieldValue(['accounts', index, 'new_dataset'], isNewDataset);
    }, [form, index, isNewDataset]);

    useEffect(() => {
        // When a row is in "dataset" mode, ensure a dataset is selected (default to first option).
        if (isReadOnly) return;
        if (isNewDataset !== false) return;
        if (!defaultDatasetFilename) return;
        const current = form.getFieldValue(['accounts', index, 'dataset_filename']);
        if (!current) {
            form.setFieldValue(['accounts', index, 'dataset_filename'], defaultDatasetFilename);
        }
    }, [defaultDatasetFilename, form, index, isNewDataset, isReadOnly]);

    const shouldShowNewDatasetFields = isNewDataset === true || (datasetsLoaded && datasets.length === 0);

    return (
        <Flex vertical style={{ position: 'relative' }}>
            {index > 0 ? <Divider size='small' style={{ borderWidth: WIDE_BORDER_WIDTH }} /> : null}

            <FormSectionTitle title={
                <Flex justify='space-between' align='center'>
                    Compte {fieldsCount > 1 ? `N°${index + 1}` : ''}
                    {creationMode === 'multiple' && fieldsCount > 1 ? (
                        <Flex justify="flex-end" gap={10}>
                            <Button
                                type="text"
                                htmlType="button"
                                onClick={onRemove}
                                icon={<MinusCircleOutlined
                                    style={{ color: 'red' }} />}
                                disabled={isReadOnly}
                            >
                                Retirer ce compte
                            </Button>
                        </Flex>
                    ) : null}
                </Flex>
            } />
            {isReadOnly ? (
                <div
                    style={{
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(16, 185, 129, 0.1)',
                        zIndex: 1,
                        fontSize: 64,
                    }}
                >
                    <CheckCircleOutlined style={{ color: '#10b981' }} />
                </div>
            ) : null}

            <FormContainer>
                <Form.Item
                    label="Prénom"
                    name={[field.name, 'user', 'firstname']}
                    rules={[
                        { required: true, message: 'Le prénom est requis.' },
                        { min: 3, message: 'Le prénom doit contenir au moins 3 caractères.' },
                    ]}
                >
                    <Input placeholder="Jean" disabled={isReadOnly} />
                </Form.Item>

                <Form.Item
                    label="Nom"
                    name={[field.name, 'user', 'lastname']}
                    rules={[
                        { required: true, message: 'Le nom est requis.' },
                        { min: 3, message: 'Le nom doit contenir au moins 3 caractères.' },
                    ]}
                >
                    <Input placeholder="Dupont" disabled={isReadOnly} />
                </Form.Item>

                <Form.Item label="Email" required>
                    {definedMailEnabled ? (
                        <Flex vertical gap={4}>
                            <Typography.Text type="secondary" copyable={definedMailPreview ? { text: definedMailPreview } : false}>
                                {definedMailPreview ?? '—'}
                            </Typography.Text>
                        </Flex>
                    ) : (
                    creationMode === 'multiple' && isManualEmail ? (
                        <Flex style={{ position: 'relative' }}>
                            <Form.Item
                                name={[field.name, 'user', 'email']}
                                style={{ width: '100%', marginBottom: 0 }}
                            >
                                <Input
                                    disabled={isReadOnly}
                                    placeholder="ddd@fff.eee"
                                    style={{ paddingRight: '2rem' }}
                                />
                            </Form.Item>
                            <Button
                                type="link"
                                htmlType="button"
                                icon={<EditOutlined />}
                                onClick={toggleEmailEdition}
                                disabled={isReadOnly}
                                style={{ position: 'absolute', right: 0, top: 0 }}
                            />
                        </Flex>
                    ) : (
                        <Flex style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 4 }}>
                            <Flex style={{ position: 'relative' }}>
                                <Form.Item
                                    noStyle
                                    shouldUpdate={(prev, cur) => {
                                        const p = prev?.accounts?.[index]?.user;
                                        const c = cur?.accounts?.[index]?.user;
                                        return p?.firstname !== c?.firstname || p?.lastname !== c?.lastname;
                                    }}
                                >
                                    {() => {
                                        const firstname = String(form.getFieldValue(['accounts', index, 'user', 'firstname']) ?? '');
                                        const lastname = String(form.getFieldValue(['accounts', index, 'user', 'lastname']) ?? '');
                                        const local = `${normalizeString(firstname)}.${normalizeString(lastname)}`
                                            .replace(/\.+/g, '.')
                                            .replace(/^\./, '')
                                            .replace(/\.$/, '');
                                        return <Input value={local} disabled style={{ paddingRight: '2rem' }} />;
                                    }}
                                </Form.Item>
                                {creationMode === 'multiple' ? (
                                    <Button
                                        type="link"
                                        htmlType="button"
                                        icon={<EditOutlined />}
                                        onClick={toggleEmailEdition}
                                        disabled={isReadOnly}
                                        style={{ position: 'absolute', right: 0, top: 0 }}
                                    />
                                ) : null}
                            </Flex>
                            <Form.Item
                                name={[field.name, 'user', 'email_domain']}
                                required
                                rules={[{ required: true, message: 'Le domaine email est requis.' }]}
                            >
                                <Select
                                    disabled={isReadOnly}
                                    options={DEMO_DOMAINS.map((domain) => ({
                                        label: domain,
                                        value: domain,
                                    }))}
                                />
                            </Form.Item>
                        </Flex>
                    )
                    )}
                </Form.Item>

                <Form.Item label="Avatar" name={[field.name, 'user', 'avatar']} required>
                    <AvatarInput disabled={isReadOnly} />
                </Form.Item>

                <Form.Item label="Nom" name={[field.name, 'subscription', 'name']} hidden required>
                    <Input placeholder="Leedcorp" />
                </Form.Item>

                {shouldShowNewDatasetFields ? (
                    <Form.Item label="Domaine" name={[field.name, 'ai', 'domain']} required>
                        <DomainsSelect
                            placeholder="Choisir un domaine"
                            disabled={isReadOnly}
                        />
                    </Form.Item>
                ) : null}

                {!isNewDataset && datasets.length > 0 ? (
                    <Form.Item
                        label="Dataset"
                        name={[field.name, 'dataset_filename']}
                        style={{ flex: 1, marginBottom: 0 }}
                        rules={[{ required: true, message: 'Veuillez sélectionner un dataset.' }]}
                    >
                        <Select
                            placeholder="Choisir un dataset"
                            disabled={isReadOnly || datasets.length === 0}
                            options={datasets.map((dataset) => ({
                                label: dataset,
                                value: dataset,
                            }))}
                        />
                    </Form.Item>
                ) : null}

                <Form.Item label="Nouveau">
                    <Switch
                        checked={Boolean(isNewDataset)}
                        disabled={isReadOnly || !datasetsLoaded || datasets.length === 0}
                        onChange={(checked) => {
                            setIsNewDataset(checked);
                            if (!checked && defaultDatasetFilename) {
                                form.setFieldValue(['accounts', index, 'dataset_filename'], defaultDatasetFilename);
                            }
                        }}
                    />
                </Form.Item>
            </FormContainer>

            {accountResult && !accountResult.success ? (
                <Typography.Text type="danger" style={{ marginTop: 6 }}>
                    {accountResult.errorMessage || 'Erreur lors de la création.'}
                </Typography.Text>
            ) : null}

            {shouldShowNewDatasetFields ? (
                <>
                    <FormSectionTitle title="Script de la démo" />
                    <FormContainer oneColumnMax>
                        <Form.Item
                            name={[field.name, 'script']}
                            rules={[
                                { required: true, message: 'Le script de la démo est requis.' },
                                { min: 100, message: 'Le script doit contenir au moins 100 caractères.' },
                            ]}
                        >
                            <Input.TextArea
                                placeholder="Décrivez les étapes et situations que vous souhaitez présenter lors de la démonstration..."
                                showCount
                                maxLength={50000}
                                rows={9}
                                disabled={isReadOnly}
                            />
                        </Form.Item>
                    </FormContainer>
                </>
            ) : null}
        </Flex>
    );
};

export default DemoAccountFormItem;

